import React, { useState, useEffect, useRef } from 'react';
import { useShop, useAuth } from '../App';
import PhotoEditorModal from './PhotoEditorModal';
import { toJpegOrPng } from '../utils/imageUpload';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Default appraisal fee
const APPRAISAL_PRICE = 75;

const DEFAULT_CHARACTERISTICS = `Item of Jewelry: 
Cast: 
Shape/Cutting Style: 
Carat Weight: 
Measurements: 
Polish: 
Symmetry: 
Color Grade: 
Clarity Grade: 
Fluorescence: 
IGI #: `;

export default function AppraisalModal({ isOpen, onClose, appraisal, customer, onSuccess, onDelete, onGoToTransaction }) {
  const { shops, currentShop } = useShop();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [showTransactionMenu, setShowTransactionMenu] = useState(false);
  const [detaching, setDetaching] = useState(false);
  const [detachedAppraisalId, setDetachedAppraisalId] = useState(null);
  const [shopId, setShopId] = useState('');
  const [formData, setFormData] = useState({
    customer_id: '',
    prepared_name: '',
    prepared_address: '',
    prepared_city_state_zip: '',
    unit_price: APPRAISAL_PRICE,
    value: '',
    prepared_date: new Date().toISOString().split('T')[0],
    taxable: true,
    taxIncluded: false,
    description: '',
    characteristics: ''
  });
  const [photos, setPhotos] = useState([]);
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [activeDescriptionTab, setActiveDescriptionTab] = useState('description');
  const searchInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError('');
      setSearchQuery('');
      setSearchResults([]);
      setShowSearchResults(false);

      if (appraisal) {
        // Editing existing appraisal
        setSelectedCustomer({
          id: appraisal.customer_id,
          first_name: appraisal.first_name || '',
          last_name: appraisal.last_name || ''
        });
        setFormData({
          customer_id: appraisal.customer_id,
          prepared_name: appraisal.prepared_name || '',
          prepared_address: appraisal.prepared_address || '',
          prepared_city_state_zip: appraisal.prepared_city_state_zip || '',
          unit_price: appraisal.unit_price || APPRAISAL_PRICE,
          value: appraisal.value || '',
          prepared_date: appraisal.prepared_date ? (typeof appraisal.prepared_date === 'string' ? appraisal.prepared_date.split('T')[0] : appraisal.prepared_date) : new Date().toISOString().split('T')[0],
          taxable: appraisal.taxable !== undefined ? appraisal.taxable : true,
          taxIncluded: appraisal.taxIncluded || false,
          description: appraisal.description || '',
          characteristics: appraisal.characteristics || ''
        });
        setPhotos(appraisal.photos || []);
        setShopId(appraisal.shop_id || '');
      } else if (customer) {
        // Pre-filled customer from context
        setSelectedCustomer(customer);
        setFormData({
          customer_id: customer.id,
          prepared_name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || '',
          prepared_address: customer.address_line1 || '',
          prepared_city_state_zip: [customer.city, customer.state, customer.postal_code].filter(Boolean).join(', ') || '',
          unit_price: APPRAISAL_PRICE,
          value: '',
          prepared_date: new Date().toISOString().split('T')[0],
          taxable: true,
          taxIncluded: false,
          description: '',
          characteristics: DEFAULT_CHARACTERISTICS
        });
        setPhotos([]);
        setShopId(currentShop?.shop_id || '');
      } else {
        setSelectedCustomer(null);
        setShopId(currentShop?.shop_id || '');
        setFormData({
          customer_id: '',
          prepared_name: '',
          prepared_address: '',
          prepared_city_state_zip: '',
          unit_price: APPRAISAL_PRICE,
          value: '',
          prepared_date: new Date().toISOString().split('T')[0],
          taxable: true,
          taxIncluded: false,
          description: '',
          characteristics: DEFAULT_CHARACTERISTICS
        });
        setPhotos([]);
      }
    }
  }, [isOpen, appraisal, customer]);

  // Search customers
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!searchQuery || searchQuery.length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/customers/search/autocomplete?q=${encodeURIComponent(searchQuery)}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.customers || []);
          setShowSearchResults(true);
        }
      } catch (err) {
        console.error('Error searching customers:', err);
      }
    };
    const timer = setTimeout(fetchCustomers, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Listen for appraisal detached events from TransactionModal
  useEffect(() => {
    const handleAppraisalDetached = (event) => {
      if (appraisal && event.detail.appraisalId === appraisal.id) {
        setDetachedAppraisalId(appraisal.id);
      }
    };
    window.addEventListener('appraisalDetached', handleAppraisalDetached);
    return () => window.removeEventListener('appraisalDetached', handleAppraisalDetached);
  }, [appraisal]);

  // Derive effective transaction_id: null if detached locally
  const effectiveTransactionId = detachedAppraisalId === appraisal?.id ? null : appraisal?.transaction_id;

  const handleDetachAppraisal = async () => {
    setShowTransactionMenu(false);
    setDetaching(true);
    try {
      const res = await fetch(`${API_URL}/appraisals/${appraisal.id}/detach`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to detach appraisal');
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setDetaching(false);
    }
  };

  const handleGoToTransaction = () => {
    setShowTransactionMenu(false);
    if (onGoToTransaction) {
      onGoToTransaction(effectiveTransactionId);
    }
  };

  const handleCustomerSelect = (cust) => {
    setSelectedCustomer(cust);
    setFormData(prev => ({
      ...prev,
      customer_id: cust.id,
      prepared_name: prev.prepared_name || `${cust.first_name || ''} ${cust.last_name || ''}`.trim() || '',
      prepared_address: prev.prepared_address || (cust.address_line1 || ''),
      prepared_city_state_zip: prev.prepared_city_state_zip || [cust.city, cust.state, cust.postal_code].filter(Boolean).join(', ') || ''
    }));
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  const handlePhotoUpload = async (input) => {
    // Support both event objects (from file input) and raw File objects (from PhotoEditorModal)
    const rawFile = input?.target?.files?.[0] || input;
    if (!rawFile) return null;

    setUploading(true);
    try {
      // Phones often hand over .webp; convert to JPEG so only jpg/png are uploaded
      const file = await toJpegOrPng(rawFile);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await fetch(`${API_URL}/uploads?folder=appraisals`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });
      if (response.ok) {
        const data = await response.json();
        // When called from file input, update photos directly
        // When called from PhotoEditorModal, return the result
        if (input?.target) {
          setPhotos([...photos, { url: data.url, filename: data.originalname }]);
        }
        return data;
      }
      return null;
    } catch (err) {
      setError('Failed to upload photo');
      return null;
    } finally {
      setUploading(false);
      if (input?.target) input.target.value = '';
    }
  };

  const handleRemovePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handlePhotoSave = (updatedPhotos) => {
    setPhotos(updatedPhotos);
    setShowPhotoEditor(false);
  };

  const handlePhotoEditorClose = () => {
    setShowPhotoEditor(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      setError('Please select a customer');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const url = appraisal ? `${API_URL}/appraisals/${appraisal.id}` : `${API_URL}/appraisals`;
      const method = appraisal ? 'PUT' : 'POST';

      const body = {
        ...formData,
        unit_price: parseFloat(formData.unit_price) || APPRAISAL_PRICE,
        value: formData.value ? parseFloat(formData.value) : null,
        tax_included: formData.taxIncluded || false,
        photos,
        shop_id: shopId
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save appraisal');
      }

      const data = await response.json();
      onSuccess(data.appraisal);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    if (appraisal) {
      window.open(`${API_URL}/appraisals/${appraisal.id}/pdf`, '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Shop ID Field */}
              {isAdmin && appraisal ? (
                <select
                  value={shopId}
                  onChange={(e) => setShopId(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">No Shop</option>
                  {shops.map((shop) => (
                    <option key={shop.id} value={shop.shop_id}>{shop.shop_id}</option>
                  ))}
                </select>
              ) : (
                shopId && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    {shopId}
                  </span>
                )
              )}
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              {appraisal ? 'Edit Appraisal' : 'New Appraisal'}
            </h2>
            <div className="flex items-center gap-2">
              {effectiveTransactionId && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowTransactionMenu(!showTransactionMenu)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    title={`Transaction #${effectiveTransactionId}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Transaction #{effectiveTransactionId}
                  </button>
                  {showTransactionMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowTransactionMenu(false)} />
                      <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-20 py-1">
                        <button
                          type="button"
                          onClick={handleGoToTransaction}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Go to Transaction
                        </button>
                        <button
                          type="button"
                          onClick={handleDetachAppraisal}
                          disabled={detaching}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          {detaching ? 'Detaching...' : 'Detach from Transaction'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              {appraisal && (
                <button
                  type="button"
                  onClick={handlePrint}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                  title="Print Appraisal PDF"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4" autoComplete="off">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Customer Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer <span className="text-red-500">*</span>
              {appraisal?.transaction_id && <span className="ml-2 text-xs text-gray-400">(Locked - attached to transaction)</span>}
            </label>
            {selectedCustomer ? (
              <div className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                <span className="text-sm">{selectedCustomer.first_name} {selectedCustomer.last_name}</span>
                {!appraisal?.transaction_id && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCustomer(null);
                      setFormData(prev => ({ ...prev, customer_id: '' }));
                      searchInputRef.current?.focus();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    &times;
                  </button>
                )}
              </div>
            ) : !appraisal?.transaction_id ? (
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  autoComplete="off"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => showSearchResults && setShowSearchResults(true)}
                  onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                  placeholder="Search customer by name or phone..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {searchResults.map(cust => (
                      <button
                        key={cust.id}
                        type="button"
                        onClick={() => handleCustomerSelect(cust)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50"
                      >
                        {cust.first_name} {cust.last_name}
                        {cust.mobile_phone && <span className="text-gray-400 ml-2">({cust.mobile_phone})</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Main Details - Two Column Layout */}
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left Column: Name, Address, CityStateZip */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  autoComplete="off"
                  value={formData.prepared_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, prepared_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  autoComplete="off"
                  value={formData.prepared_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, prepared_address: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City, State, Zip</label>
                <input
                  type="text"
                  autoComplete="off"
                  value={formData.prepared_city_state_zip}
                  onChange={(e) => setFormData(prev => ({ ...prev, prepared_city_state_zip: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="City, State, Zip"
                />
              </div>
            </div>

            {/* Right Column: Date, Appraised Value, Price + Taxable */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  autoComplete="off"
                  value={formData.prepared_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, prepared_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Appraised Value</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    autoComplete="off"
                    step="0.01"
                    min="0"
                    value={formData.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                    className="w-full pl-7 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      autoComplete="off"
                      step="0.01"
                      min="0"
                      value={formData.unit_price}
                      onChange={(e) => setFormData(prev => ({ ...prev, unit_price: e.target.value }))}
                      className="w-full pl-7 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4 pt-5">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.taxable}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData({ ...formData, taxable: !formData.taxable });
                      }}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Taxable</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.taxIncluded || false}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData({ ...formData, taxIncluded: !formData.taxIncluded });
                      }}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Tax Included</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Description and Characteristics - Tabbed */}
          <div className="mb-4">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-4">
                <button
                  type="button"
                  onClick={() => setActiveDescriptionTab('description')}
                  className={`py-2 px-4 border-b-2 font-medium text-sm ${
                    activeDescriptionTab === 'description'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Description
                </button>
                <button
                  type="button"
                  onClick={() => setActiveDescriptionTab('characteristics')}
                  className={`py-2 px-4 border-b-2 font-medium text-sm ${
                    activeDescriptionTab === 'characteristics'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Characteristics
                </button>
                <button
                  type="button"
                  onClick={() => setActiveDescriptionTab('photos')}
                  className={`py-2 px-4 border-b-2 font-medium text-sm flex items-center gap-1.5 ${
                    activeDescriptionTab === 'photos'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Photos
                  {photos.length > 0 && (
                    <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                      {photos.length}
                    </span>
                  )}
                </button>
              </nav>
            </div>
            <div className="mt-3">
              {activeDescriptionTab === 'description' ? (
                <textarea
                  autoComplete="off"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={11}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Description"
                />
              ) : activeDescriptionTab === 'characteristics' ? (
                <textarea
                  autoComplete="off"
                  value={formData.characteristics}
                  onChange={(e) => setFormData(prev => ({ ...prev, characteristics: e.target.value }))}
                  rows={11}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Characteristics"
                />
              ) : (
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      {uploading ? 'Uploading...' : '+ Upload Photo'}
                    </button>
                    <span className="text-xs text-gray-400">{photos.length} photo(s)</span>
                  </div>
                  {photos.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {photos.map((photo, index) => (
                        <div key={index} className="relative w-20 h-20">
                          <img
                            src={photo.url}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-full object-cover rounded-md border border-gray-200 cursor-pointer"
                            onClick={() => setShowPhotoEditor(true)}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(index)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t bg-gray-50">
          <div className="flex gap-2">
            {appraisal && onDelete && !effectiveTransactionId ? (
              <button
                type="button"
                onClick={() => { onDelete(appraisal.id); onClose(); }}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50"
              >
                Delete Appraisal
              </button>
            ) : <div />}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : appraisal ? 'Update Appraisal' : 'Create Appraisal'}
          </button>
          </div>
        </div>
      </div>

      {/* Photo Editor Modal */}
      <PhotoEditorModal
        isOpen={showPhotoEditor}
        onClose={handlePhotoEditorClose}
        photos={photos}
        onSave={handlePhotoSave}
        onUpload={handlePhotoUpload}
      />
    </div>
  );
}
